// src/data/hotspotExplanations.js

/**
 * HOTSPOT EXPLANATION PARAGRAPHS
 * 
 * Contains explanatory text for each hotspot category and severity level.
 * Displayed when user clicks on a hotspot in focus mode.
 * 
 * Categories:
 * - top: Crown/top of head thinning
 * - receding: Hairline recession (pattern baldness)
 * - temples: Temple area recession
 * 
 * Severity Levels (1-7):
 * 1-2: Minimal to no hair loss
 * 3: Minor hair loss beginning
 * 4: Noticeable/significant hair loss
 * 5: Moderate to advanced hair loss
 * 6: Advanced hair loss
 * 7: Severe/extensive hair loss
 */

export const HOTSPOT_CATEGORIES = {
  TOP: 'top',
  RECEDING: 'receding',
  TEMPLES: 'temples',
};

export const SEVERITY_LABELS = {
  1: 'Minimal',
  2: 'Very Low',
  3: 'Minor',
  4: 'Significant',
  5: 'Moderate',
  6: 'Advanced',
  7: 'Severe',
};

/**
 * Get the display title for a hotspot category
 */
export function getCategoryTitle(category) {
  switch (category) {
    case HOTSPOT_CATEGORIES.TOP:
      return 'Frontal Thinning';
    case HOTSPOT_CATEGORIES.RECEDING:
      return 'Temporal Recession';
    case HOTSPOT_CATEGORIES.TEMPLES:
      return 'Sides Thinning';
    default:
      return 'Hair Analysis';
  }
}

/**
 * Get the subtitle showing category type and severity
 */
export function getCategorySubtitle(category, severity) {
  const severityLabel = SEVERITY_LABELS[severity] || 'Unknown';
  return severityLabel;
}

/**
 * Explanation paragraphs organized by category and severity level
 * 
 * Structure: EXPLANATIONS[category][severity] = string
 * 
 * Each paragraph should be:
 * - 2-4 sentences
 * - Educational and informative
 * - Affirming and non-alarming tone
 * - Specific to the category and severity
 */
export const EXPLANATIONS = {
  // ========================================
  // TOP (Crown/Vertex Thinning)
  // ========================================
    [HOTSPOT_CATEGORIES.TOP]: {
    1: "Your frontal scalp area shows strong, uniform density with no visible thinning. This pattern reflects a healthy growth cycle and excellent follicular coverage. At this stage, simple maintenance is typically sufficient.",
    
    2: "The frontal scalp maintains good overall density with only subtle variation that falls within normal limits. These changes are not considered hair loss and are common across many age groups. Ongoing observation is usually all that’s needed here.",
    
    3: "Clear thinning is present in the frontal scalp area and is visually noticeable. This is a common and meaningful stage of hair loss, but still one with very high treatment success. Early action here often prevents rapid progression.",
    
    4: "The frontal scalp shows pronounced thinning with scalp visibility. This level of loss affects overall appearance but remains highly treatable. Many patients see strong cosmetic improvement with the right intervention plan.",
    
    5: "Moderate-to-advanced thinning is evident across the frontal scalp, with reduced density throughout the area. While regrowth potential varies, restoration and density enhancement are very achievable.",
    
    6: "Advanced frontal scalp thinning has resulted in broad scalp exposure and limited natural coverage. Recovery is still possible, though it may require higher graft numbers. The focus should be toward restoring visual density.",
    
    7: "Extensive hair loss dominates the frontal scalp region, representing an advanced stage. While full density may not be fully recoverable, modern treatments can still deliver meaningful aesthetic improvement. Personalized planning is essential.",
    },

    // ========================================
    // RECEDING (Center Hairline Recession)
    // ========================================
    [HOTSPOT_CATEGORIES.RECEDING]: {
      1: "Your hairline shows no signs of temporal recession, and the natural curve remains intact. The overall shape appears balanced and youthful. This is an excellent baseline to preserve.",
      
      2: "Mild temporal changes are present but remain within the range of normal hairline maturation. The frontal outline is still well defined and stable.",
      
      3: "Noticeable temporal recession is present, beginning to form an early M-shaped hairline. This level of recession is clearly visible and considered significant. Fortunately, treatment at this stage is often highly effective.",
      
      4: "Clear M-shaped recession has developed, with visible thinning along the frontal-temporal zones. This is a common pattern in androgen-related hair loss and has a strong visual impact. Restoration is still very achievable here.",
      
      5: "Moderate M-shaped recession has shifted the frontal hairline backward and reduced its definition. The pattern is well established but remains highly workable. Treatment typically focuses on reinforcing the frontal-temporal areas.",
      
      6: "Advanced temporal recession has significantly altered the hairline. While natural regrowth may be limited, modern restoration techniques can reliably rebuild structure. At this stage, higher graft numbers are often needed.",
      
      7: "Extensive temporal recession has resulted in an advanced recession. Full reversal may not be realistic, but substantial cosmetic improvement is still achievable. Careful planning is essential to achieve natural results.",
    },

    // ========================================
    // TEMPLES (Temple Area Recession)
    // ========================================
    [HOTSPOT_CATEGORIES.TEMPLES]: {
      1: "The sides of your head show strong, uniform density with no signs of thinning. This area appears healthy and stable. It may serve as a very good donor region if needed in the future.",
      
      2: "Mild thinning is present on the sides but remains within normal variation. Density is still largely intact and consistent. At this level, donor quality is generally considered favorable.",
      
      3: "Noticeable thinning is present along the sides of the head and is clearly visible. While less common than top thinning, this pattern is clinically relevant. It warrants closer observation over time.",
      
      4: "The sides show clear and established thinning with reduced overall density. This pattern may affect donor reliability. A professional evaluation is usually recommended to assess stability.",
      
      5: "Moderate side thinning is evident, with visibly weakened hair coverage. This can significantly impact donor availability and graft planning. Careful examination is important at this stage.",
      
      6: "Advanced thinning has affected large portions of the sides, reducing usable density. Donor quality and total graft count may be limited. Detailed assessment by a specialist is essential here.",
      
      7: "Extensive thinning is present across the sides of the head, indicating advanced involvement. This pattern often complicates donor planning significantly. Thorough clinical evaluation is required to determine realistic options.",
    },
};

/**
 * Get explanation text for a specific hotspot
 * @param {string} category - One of HOTSPOT_CATEGORIES values
 * @param {number} severity - Severity level 1-7
 * @returns {string} Explanation paragraph
 */
export function getExplanation(category, severity) {
  // Clamp severity to valid range
  const clampedSeverity = Math.max(1, Math.min(7, Math.round(severity)));
  
  const categoryExplanations = EXPLANATIONS[category];
  if (!categoryExplanations) {
    return "Analysis information is not available for this area.";
  }
  
  return categoryExplanations[clampedSeverity] || "Analysis information is not available for this severity level.";
}

/**
 * Map hotspot ID to category
 * @param {string} hotspotId - The hotspot ID (e.g., 'top-thinning', 'pattern-receding-left')
 * @returns {string} Category key
 */
export function getHotspotCategory(hotspotId) {
  if (hotspotId.includes('top-thinning') || hotspotId.includes('top')) {
    return HOTSPOT_CATEGORIES.TOP;
  }
  // Check temples BEFORE receding (since 'temples-receding' contains 'receding')
  if (hotspotId.includes('temples-receding') || hotspotId.includes('temple')) {
    return HOTSPOT_CATEGORIES.TEMPLES;
  }
  if (hotspotId.includes('pattern-receding') || hotspotId.includes('receding')) {
    return HOTSPOT_CATEGORIES.RECEDING;
  }
  return HOTSPOT_CATEGORIES.TOP; // Default fallback
}

export default {
  HOTSPOT_CATEGORIES,
  SEVERITY_LABELS,
  EXPLANATIONS,
  getCategoryTitle,
  getCategorySubtitle,
  getExplanation,
  getHotspotCategory,
};
